import { Outlet, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { TextureOverlay } from "@/components/shared/TextureOverlay";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useOngoingIncidents } from "@/hooks/useIncidents";
import { CookieConsent } from "@/components/shared/CookieConsent";
import { UptimeWingLogo } from "@/components/shared/UptimeWingLogo";
import { RealtimeStatusIndicator } from "./RealtimeStatusIndicator";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { AlertCircle, LayoutDashboard, AlertOctagon, Share2, Settings as SettingsIcon } from "lucide-react";
import { NiceLoader } from "@/components/shared/NiceLoader";

export function AppLayout() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: incidents = [] } = useOngoingIncidents();

  if (loading || profileLoading) {
    return <NiceLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  // Check if any website or heartbeat is currently down
  const activeIncident = incidents.find((inc: any) => {
    const s = inc.websites?.status;
    return !(s === "up" || s === "passing");
  });

  const initials = user?.name
    ? user.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()
    : (user?.email ? user.email.slice(0, 2).toUpperCase() : "U");

  const displayName = user?.name || user?.email?.split("@")[0] || "User";

  return (
    <div className="relative min-h-screen flex flex-col w-full bg-background text-foreground">
      <TextureOverlay />
      <CookieConsent />

      {/* Red banner above header if incident is active */}
      {activeIncident && (
        <Link
          to={`/dashboard?incident=${activeIncident.id}`}
          className="bg-status-down text-white text-xs font-black uppercase tracking-wider py-2.5 px-4 flex items-center justify-center gap-2 border-b-2 border-black animate-pulse z-50 text-center select-none"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>ACTIVE INCIDENT: {activeIncident.websites?.name || activeIncident.websites?.url?.replace(/^https?:\/\//i, "")} DOWN — VIEW FIX</span>
        </Link>
      )}

      {/* Desktop Header */}
      <header className="sticky top-0 z-40 bg-background h-16 flex items-center justify-between px-6 border-b-4 border-foreground select-none">
        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <Link to="/dashboard" className="flex items-center gap-2">
            <UptimeWingLogo className="h-7 w-7 text-black dark:text-white" />
            <span className="text-sm font-black tracking-tighter uppercase">UPTIMEWING</span>
          </Link>
        </div>

        {/* Desktop Nav: Dashboard | Incidents | Integrations | Settings */}
        <nav className="hidden md:flex items-center gap-8 text-xs font-black uppercase tracking-widest">
          <Link
            to="/dashboard"
            className={`py-1 hover:underline transition-all ${
              location.pathname === "/dashboard" ? "border-b-2 border-foreground text-foreground" : "text-muted-foreground"
            }`}
          >
            Dashboard
          </Link>
          <Link
            to="/services"
            className={`py-1 hover:underline transition-all ${
              location.pathname.startsWith("/services") ? "border-b-2 border-foreground text-foreground" : "text-muted-foreground"
            }`}
          >
            Services
          </Link>
          <Link
            to="/incidents"
            className={`py-1 hover:underline transition-all relative ${
              location.pathname.startsWith("/incidents") ? "border-b-2 border-foreground text-foreground" : "text-muted-foreground"
            }`}
          >
            Incidents
            {incidents.length > 0 && (
              <span className="absolute -top-1.5 -right-2.5 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-status-down opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-status-down"></span>
              </span>
            )}
          </Link>
          <Link
            to="/integrations"
            className={`py-1 hover:underline transition-all ${
              location.pathname.startsWith("/integrations") ? "border-b-2 border-foreground text-foreground" : "text-muted-foreground"
            }`}
          >
            Integrations
          </Link>
          <Link
            to="/settings"
            className={`py-1 hover:underline transition-all ${
              location.pathname.startsWith("/settings") ? "border-b-2 border-foreground text-foreground" : "text-muted-foreground"
            }`}
          >
            Settings
          </Link>
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-4">
          <RealtimeStatusIndicator />
          <ThemeToggle />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-none p-1.5 focus:outline-none max-w-[120px]">
                <Avatar className="h-7 w-7 rounded-none shrink-0 border-2 border-black">
                  {user?.avatar_url && (
                    <AvatarImage src={user.avatar_url} alt={displayName} className="object-cover" />
                  )}
                  <AvatarFallback className="rounded-none bg-foreground text-background text-[10px] font-bold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-[10px] font-mono font-black uppercase text-zinc-500 truncate">
                  [● {displayName}]
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 rounded-none border-2 border-black shadow-[4px_4px_0_0_#000] p-2">
              <div className="px-2 py-1.5 text-[10px] font-mono font-black text-zinc-400 border-b border-black/10 mb-1">
                SIGNED IN AS {user?.email}
              </div>
              <DropdownMenuItem onClick={() => navigate("/settings?tab=plan")} className="text-xs font-black uppercase tracking-wider cursor-pointer">
                Team & Billing
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/status-pages-builder")} className="text-xs font-black uppercase tracking-wider cursor-pointer">
                Status Page
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/docs")} className="text-xs font-black uppercase tracking-wider cursor-pointer">
                Docs
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-xs font-black uppercase tracking-wider text-status-down cursor-pointer border-t border-black/10 mt-1">
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="w-full max-w-6xl mx-auto px-4 md:px-8 py-8 flex-1 pb-24 md:pb-12">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background h-16 border-t-4 border-black flex items-center justify-around px-4 select-none">
        <Link
          to="/dashboard"
          className={`flex flex-col items-center justify-center flex-1 h-full gap-1 ${
            location.pathname === "/dashboard" ? "text-primary font-black" : "text-muted-foreground"
          }`}
        >
          <LayoutDashboard className="h-5 w-5" />
          <span className="text-[9px] font-black uppercase tracking-widest">Dashboard</span>
        </Link>
        <Link
          to="/services"
          className={`flex flex-col items-center justify-center flex-1 h-full gap-1 ${
            location.pathname.startsWith("/services") ? "text-primary font-black" : "text-muted-foreground"
          }`}
        >
          <AlertCircle className="h-5 w-5" />
          <span className="text-[9px] font-black uppercase tracking-widest">Services</span>
        </Link>
        <Link
          to="/incidents"
          className={`flex flex-col items-center justify-center flex-1 h-full gap-1 relative ${
            location.pathname.startsWith("/incidents") ? "text-primary font-black" : "text-muted-foreground"
          }`}
        >
          <AlertOctagon className="h-5 w-5" />
          <span className="text-[9px] font-black uppercase tracking-widest">Incidents</span>
          {activeIncident && (
            <span className="absolute top-2.5 right-6 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-status-down opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-status-down"></span>
            </span>
          )}
        </Link>
        <Link
          to="/settings"
          className={`flex flex-col items-center justify-center flex-1 h-full gap-1 ${
            location.pathname.startsWith("/settings") ? "text-primary font-black" : "text-muted-foreground"
          }`}
        >
          <SettingsIcon className="h-5 w-5" />
          <span className="text-[9px] font-black uppercase tracking-widest">Settings</span>
        </Link>
      </nav>
    </div>
  );
}
